import {useRef, useState} from 'react';

import {Typography, Grid, useTheme, Tooltip} from '@mui/material';
import Box from '@mui/material/Box';

import useProfile from '../../../../hooks/providers/useProfile';
import {onboardingFlowStepShape} from '../../../../proptypes-shapes';
import CustomizedLinearProgress from '../../../progress/LinearProgress';

const OnboardingSingleStepProgression = ({step}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [hoveredTime, setHoveredTime] = useState(0);
  const progressRef = useRef(null);

  const {videoRef, currentFlowStep, currentVideoDuration, saveOnboardingVideoProgression, setCurrentFlowStepId} = useProfile();
  const theme = useTheme();

  const isCurrentStep = step.step_id === currentFlowStep.step_id;
  const titleColor = isCurrentStep ? theme.palette.secondary.main : 'text.disabled';

  const goToStep = () => {
    const video = videoRef.current;
    if (isCurrentStep && video && step.url_video) {
      video.currentTime = hoveredTime;
    } else {
      saveOnboardingVideoProgression(currentFlowStep.step_id, currentFlowStep.position);
      setCurrentFlowStepId(step.step_id);
    }
  };

  const formatTime = seconds => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const onMouseMove = e => {
    if (!isCurrentStep || !progressRef.current) return;

    const rect = progressRef.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const percentage = Math.min(Math.max(offsetX / rect.width, 0), 1); // clamp between 0 and 1

    const seconds = percentage * currentVideoDuration;
    setHoveredTime(seconds);
    setIsHovered(true);
  };

  const progressionPercentage = step.url_video ? step.position : 0;

  return (
    <Tooltip
      placement="top"
      title={isHovered && isCurrentStep && step.url_video ? formatTime(hoveredTime) : ''}
      PopperProps={{
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, -5]
            }
          }
        ]
      }}
      componentsProps={{
        tooltip: {
          sx: {
            fontSize: '1rem',
            padding: '86px 10',
            borderRadius: 1,
            backgroundColor: 'grey.700'
          }
        }
      }}
    >
      <Grid item xs={4} sm={3} md onClick={goToStep} sx={{cursor: 'pointer'}} data-testid="onboarding-single-step">
        <Box ref={progressRef} onMouseMove={onMouseMove} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
          <CustomizedLinearProgress
            emptyColor={isCurrentStep ? `${theme.palette.secondary.main}30` : theme.palette.grey[200]}
            variant="determinate"
            value={progressionPercentage}
            stylesOverrides={{
              height: isCurrentStep ? 12 : 6,
              marginTop: isCurrentStep ? 0 : 6,
              minWidth: 100
            }}
          />
        </Box>
        <Typography textAlign="center" color={titleColor} fontSize={13} mt={1.5}>
          {step.title}
        </Typography>
      </Grid>
    </Tooltip>
  );
};

OnboardingSingleStepProgression.propTypes = {
  step: onboardingFlowStepShape.isRequired
};

export default OnboardingSingleStepProgression;
